import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { Field } from "@/components/common";

const MON = (d) => (d ? `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+d.slice(5, 7) - 1]} ${d.slice(0, 4)}` : "");
const EMPTY = { bill_no: "", bill_date: today(), taxable: "", cgst: "", sgst: "", igst: "", gstin: "" };

function BillDialog({ t, open, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) { setF({ ...EMPTY, ...(t.vendor_bill || {}) }); } }, [open, t.vendor_bill]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const { data } = await api.put(`/market/trips/${t.id}/vendor-bill`, { ...f, ...Object.fromEntries(["taxable", "cgst", "sgst", "igst"].map((k) => [k, Number(f[k] || 0)])) });
      toast.success(`Vendor bill saved · ITC ${MON(data.bill_date)} me`); onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="vendor-bill-dialog">
        <DialogHeader><DialogTitle>Vendor GST Bill — LR {t.lr_number}</DialogTitle>
          <DialogDescription>Trip {fmtDate(t.booking_date)} ki hai. ITC sirf bill date ke mahine me claim hoga.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor Bill No *"><Input data-testid="vendor-bill-no" value={f.bill_no} onChange={set("bill_no")} /></Field>
          <Field label="Bill Date *"><Input type="date" data-testid="vendor-bill-date" value={f.bill_date} onChange={set("bill_date")} /></Field>
          <Field label="Vendor GSTIN"><Input data-testid="vendor-bill-gstin" value={f.gstin} onChange={set("gstin")} /></Field>
          <Field label="Taxable Value"><Input type="number" data-testid="vendor-bill-taxable" value={f.taxable} onChange={set("taxable")} /></Field>
          {["cgst", "sgst", "igst"].map((k) => <Field key={k} label={k.toUpperCase()}><Input type="number" data-testid={`vendor-bill-${k}`} value={f[k]} onChange={set(k)} /></Field>)}
        </div>
        <DialogFooter><Button data-testid="vendor-bill-save" onClick={save}>Save Vendor Bill</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorBillCard({ t, onChange }) {
  const [open, setOpen] = useState(false);
  const vb = t.vendor_bill;
  const del = async () => {
    if (!window.confirm("Remove vendor bill?")) return;
    try { await api.delete(`/market/trips/${t.id}/vendor-bill`); toast.success("Removed"); onChange(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const gst = vb ? (vb.cgst || 0) + (vb.sgst || 0) + (vb.igst || 0) : 0;
  const tripDay = t.trip_date || t.booking_date || "";
  const split = vb && vb.bill_date.slice(0, 7) !== tripDay.slice(0, 7);
  return (
    <div className="rounded-xl border bg-card p-5" data-testid="vendor-bill-card">
      <div className="mb-3 flex items-center justify-between"><h3 className="font-semibold">Vendor GST Bill (ITC)</h3>
        <Button size="sm" variant="outline" data-testid="vendor-bill-edit" onClick={() => setOpen(true)}><Receipt className="mr-1 h-3.5 w-3.5" />{vb ? "Edit" : "Add Vendor Bill"}</Button></div>
      {!vb ? <p className="text-sm text-muted-foreground" data-testid="vendor-bill-empty">Vendor ka bill abhi darj nahi hua.</p> : <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span>Bill <b>{vb.bill_no}</b> · {fmtDate(vb.bill_date)}</span><button data-testid="vendor-bill-delete" onClick={del} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button></div>
        <div className="text-muted-foreground">Taxable {inr(vb.taxable, 2)} · GST {inr(gst, 2)}{vb.gstin && ` · ${vb.gstin}`}</div>
        <div className="font-semibold text-emerald-700" data-testid="vendor-bill-itc-month">ITC claim: {MON(vb.bill_date)} GST ledger</div>
        {split && <div className="inline-block rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold text-sky-800" data-testid="vendor-bill-audit">[Trip in {MON(tripDay)} - Vendor billed in {MON(vb.bill_date)}]</div>}
      </div>}
      <BillDialog t={t} open={open} onClose={() => setOpen(false)} onSaved={onChange} />
    </div>
  );
}
